import { inject, Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { LanguageService } from '../../core/services/language.service';
import { CONFIDENCE_THRESHOLD, DEMAND_LEVEL, OCR_DOC_COUNTRY_TYPE, WorkflowStep } from '../models/workflows.models';
import { DOCS_TYPE, OPERATION_TYPE, POL_TEST, POL_TYPE } from '../../operations/models/operations.models';
import { WORKFLOW_FORM_I18N_KEYS } from '../constants/workflow-form-defaults.constants';
import { WorkflowFormDefaultsService } from './workflow-form-defaults.service';
import {
	localeTextRequireAllLocalesWhenAnyFilledValidator,
	trimmedLocaleTextRequiredValidator,
	trimmedRichTextLocaleRequiredValidator,
} from '../utils/workflow-locale-text.util';
import {
	patchFormFieldFromApi,
	resolveMatchOcrFlag as resolveMatchOcrFlagUtil,
	serializeFormFieldToApi,
	stripMatchOcrFromValidators,
	withMatchOcrInValidators,
} from '../utils/workflow-form.util';

/**
 * Factories / schema FormGroup del editor de workflows (NIF-530).
 *
 * Abrir cuando: crear o ajustar estructura de controles (form principal, OCR fraud, doc, FORM step/field).
 * No contiene listado/menús: eso vive en `WorkflowsHelperService` (fachada que delega aquí).
 * Mapa: `../WORKFLOWS_EDITOR.md`.
 */
@Injectable({
	providedIn: 'root',
})
export class WorkflowFormBuilderService {
	private langSvc = inject(LanguageService);
	private workflowFormDefaults = inject(WorkflowFormDefaultsService);

	/**
	 * FormGroup con un control por locale permitido (mismo texto inicial opcional en todos).
	 *
	 * @param initialSingle - Texto inicial repetido en cada locale (default `''`)
	 * @param requireAllLocales - Si `true`, cada locale exige texto no vacío
	 * @returns FormGroup cuyas keys son códigos de idioma (`es`, `en`, …)
	 */
	public createLocaleTextFormGroup(initialSingle?: string, requireAllLocales = false): FormGroup {
		const allowed = this.langSvc.getAllowedLanguages();
		const controls: Record<string, FormControl> = {};
		const validators = requireAllLocales ? [trimmedLocaleTextRequiredValidator()] : [];
		for (const l of allowed) {
			controls[l] = new FormControl(initialSingle ?? '', validators);
		}
		return new FormGroup(controls);
	}

	/**
	 * FormGroup multi-locale con un valor inicial distinto por idioma
	 * (p. ej. copys de defaults i18n distintos en `es` y `en`).
	 *
	 * @param defaultsByLocale - Mapa locale → texto; fallback a `es` si falta la clave
	 * @param requireAllLocales - Si `true`, todos los locales obligatorios
	 * @returns FormGroup cuyas keys son códigos de idioma
	 */
	public createLocaleTextFormGroupWithDefaults(
		defaultsByLocale: Readonly<Record<string, string>>,
		requireAllLocales = false,
	): FormGroup {
		const allowed = this.langSvc.getAllowedLanguages();
		const controls: Record<string, FormControl> = {};
		const validators = requireAllLocales ? [trimmedLocaleTextRequiredValidator()] : [];
		for (const l of allowed) {
			const initial = defaultsByLocale[l] ?? defaultsByLocale['es'] ?? '';
			controls[l] = new FormControl(initial, validators);
		}
		return new FormGroup(controls);
	}

	/**
	 * Activa o quita obligatoriedad en todos los locales de un grupo
	 * (p. ej. si la operación no está disponible, se quitan required).
	 *
	 * @param group - FormGroup multi-locale o `null`/`undefined` (no-op)
	 * @param required - `true` → `trimmedLocaleTextRequiredValidator` en cada locale
	 */
	public setLocaleTextGroupRequired(group: FormGroup | null | undefined, required: boolean): void {
		if (!group) {
			return;
		}
		const allowed = this.langSvc.getAllowedLanguages();
		const validators = required ? [trimmedLocaleTextRequiredValidator()] : null;
		for (const l of allowed) {
			const c = group.get(l);
			if (c) {
				c.setValidators(validators);
				c.updateValueAndValidity({ emitEvent: false });
			}
		}
		group.updateValueAndValidity({ emitEvent: false });
	}

	/**
	 * Título de documento: obligatorio en todos los idiomas permitidos.
	 *
	 * @returns FormGroup multi-locale con `requireAllLocales=true`
	 */
	public createDocTitleLocaleFormGroup(): FormGroup {
		return this.createLocaleTextFormGroup('', true);
	}

	/**
	 * Pautas del modal documento (default).
	 * Sin texto en ningún idioma → válido; si hay texto en alguno → todos obligatorios.
	 * GENÉRICO usa {@link applyDocInstructionsValidatorsForType} (siempre obligatorio).
	 *
	 * @returns FormGroup con validador “when any filled”
	 */
	private createDocInstructionsLocaleFormGroup(): FormGroup {
		const g = this.createLocaleTextFormGroup('', false);
		g.setValidators([localeTextRequireAllLocalesWhenAnyFilledValidator()]);
		return g;
	}

	/**
	 * Pauta del paso FORM (Figma «Pauta*»): obligatoria en todos los idiomas permitidos.
	 *
	 * @param guidelineGroup - Control `step_name` del paso
	 * @param _preset - Reservado (API histórica del helper); no altera la regla actual
	 */
	public applyFormStepGuidelineValidatorsForPreset(
		guidelineGroup: FormGroup | null | undefined,
		_preset?: { fieldMetadataLocked?: boolean; prefillGuideline?: boolean } | null,
	): void {
		if (!guidelineGroup) {
			return;
		}
		guidelineGroup.clearValidators();
		this.setLocaleTextGroupRequired(guidelineGroup, true);
		guidelineGroup.updateValueAndValidity({ emitEvent: false });
	}

	/**
	 * Descripción del paso (Figma sin asterisco): opcional;
	 * si hay texto en algún idioma, todos los locales pasan a obligatorios.
	 *
	 * @param descriptionGroup - Control `step_description` del paso
	 */
	public applyFormStepDescriptionValidators(descriptionGroup: FormGroup | null | undefined): void {
		if (!descriptionGroup) {
			return;
		}
		descriptionGroup.clearValidators();
		this.setLocaleTextGroupRequired(descriptionGroup, false);
		descriptionGroup.setValidators([localeTextRequireAllLocalesWhenAnyFilledValidator()]);
		descriptionGroup.updateValueAndValidity({ emitEvent: false });
	}

	/**
	 * Ajusta validación de pautas según tipo de documento en el modal:
	 * - GENÉRICO: obligatorio en todos los idiomas permitidos.
	 * - Resto: opcional si todo vacío; si hay texto en algún idioma, obligatorio completar todos.
	 *
	 * @param instructionsGroup - FormGroup de pautas del doc
	 * @param docType - `DOCS_TYPE.*` (comparado con `GENERIC`)
	 */
	public applyDocInstructionsValidatorsForType(
		instructionsGroup: FormGroup | null | undefined,
		docType: string,
	): void {
		if (!instructionsGroup) {
			return;
		}
		const isGeneric = docType === DOCS_TYPE.GENERIC;
		instructionsGroup.clearValidators();
		if (isGeneric) {
			this.setLocaleTextGroupRequired(instructionsGroup, true);
		} else {
			this.setLocaleTextGroupRequired(instructionsGroup, false);
			instructionsGroup.setValidators([localeTextRequireAllLocalesWhenAnyFilledValidator()]);
		}
		instructionsGroup.updateValueAndValidity({ emitEvent: false });
	}

	/**
	 * Árbol FormGroup completo del editor (GENERAL + OPERATIONS + CONFIRMATION).
	 * Cada llamada crea una **instancia nueva** (mismo comportamiento legacy del helper).
	 *
	 * Schema por bloque: {@link buildGeneralFormGroup}, {@link buildOperationsFormGroup},
	 * {@link buildConfirmationFormGroup}.
	 *
	 * @returns FormGroup raíz del editor de workflows
	 */
	public get form(): FormGroup {
		return new FormGroup({
			[WorkflowStep.GENERAL]: this.buildGeneralFormGroup(),
			[WorkflowStep.OPERATIONS]: this.buildOperationsFormGroup(),
			[WorkflowStep.CONFIRMATION]: this.buildConfirmationFormGroup(),
		});
	}

	/**
	 * Paso GENERAL: alias, compañía, api_key, tema, aviso diferido, antifraud, validación.
	 *
	 * Abrir cuando: cambias campos de configuración global del workflow (no una operación).
	 *
	 * @returns FormGroup del step `WorkflowStep.GENERAL`
	 */
	private buildGeneralFormGroup(): FormGroup {
		return new FormGroup({
			allowed_device: new FormControl(['mobile', 'pc'], Validators.required),
			version: new FormControl(3),
			metadata: new FormControl(null),
			has_saved_state: new FormControl('true', Validators.required),
			location: new FormControl(true),
			notify: new FormControl(null),
			required_recording: new FormControl(false),
			include_images: new FormControl(true),
			token_duration_minutes: new FormControl(120, Validators.required),
			override_data_retention: new FormControl(false),
			document_retention_days: new FormControl(null),
			media_retention_days: new FormControl(null),
			info_retention_days: new FormControl(null),
			alias: new FormControl('', Validators.required),
			company: new FormControl('', Validators.required),
			api_key: new FormControl('', Validators.required),
			delayed_process_warning: new FormControl(true),
			delayed_process_warning_visibility: new FormControl('start_and_resume', Validators.required),
			delayed_process_warning_text_type: new FormControl('default', Validators.required),
			delayed_process_warning_text: this.createLocaleTextFormGroup(),
			antifraud: new FormGroup({
				ip_block: new FormControl(true),
				identifier_block: new FormControl(true),
				face_block: new FormControl(true),
				block_operation: new FormControl(true),
				email_alert: new FormControl(''),
				available_email_alert: new FormControl(true), // Formulario de ayuda (UI), no va al API
			}),
			sdk: new FormControl(false),
			validation: new FormControl('auto', Validators.required),
			validation_email: new FormControl(''),
			// Multi-select: aprobados y/o rechazados (ambos → API ok-ko).
			validation_manual_type: new FormControl<Array<'approved' | 'rejected'>>(['approved']),
			validation_manual_errors: new FormControl<string[]>([]),
			theme: new FormControl('', Validators.required),
		});
	}

	/**
	 * Contenedor `operations.*`: una clave por `OPERATION_TYPE`.
	 * Cada op se construye en su `build*OperationFormGroup` (abrir solo la que toques).
	 *
	 * @returns FormGroup del step `WorkflowStep.OPERATIONS`
	 */
	private buildOperationsFormGroup(): FormGroup {
		return new FormGroup({
			[OPERATION_TYPE.HOME]: this.buildHomeOperationFormGroup(),
			[OPERATION_TYPE.FORM]: this.buildFormOperationFormGroup(),
			[OPERATION_TYPE.OCR]: this.buildOcrOperationFormGroup(),
			[OPERATION_TYPE.POL]: this.buildPolOperationFormGroup(),
			[OPERATION_TYPE.ATTORNEY_POWERS]: this.buildAttorneyPowersOperationFormGroup(),
			[OPERATION_TYPE.DOCUMENTS]: this.buildDocumentsOperationFormGroup(),
			[OPERATION_TYPE.CONNECTIONS]: this.buildConnectionsOperationFormGroup(),
			[OPERATION_TYPE.FINISH]: this.buildFinishOperationFormGroup(),
		});
	}

	/**
	 * Paso final del wizard (placeholder `todo`; no se serializa al API).
	 *
	 * @returns FormGroup del step `WorkflowStep.CONFIRMATION`
	 */
	private buildConfirmationFormGroup(): FormGroup {
		return new FormGroup({
			todo: new FormControl(true),
		});
	}

	/**
	 * Schema HOME: políticas Tecalis + policy_checks + description multi-locale.
	 *
	 * @returns FormGroup `operations.HOME`
	 */
	private buildHomeOperationFormGroup(): FormGroup {
		return new FormGroup({
			available: new FormControl(true),
			tecalis_policy_check: new FormControl(true),
			policy_checks: new FormArray([]),
			description: this.createLocaleTextFormGroupWithDefaults(
				this.workflowFormDefaults.getLocaleMapForKey(WORKFLOW_FORM_I18N_KEYS.HOME_FORM_DESCRIPTION),
				false,
			),
			uuid: new FormControl(''),
			completed: new FormControl(false),
		});
	}

	/**
	 * Schema FORM: tutorial + FormArray de pasos (vacío al crear; se rellena en el mapper/UI).
	 * `autofill_from_ocr` null = sin preferencia NIF-657 (modal pendiente).
	 *
	 * @returns FormGroup `operations.FORM`
	 */
	private buildFormOperationFormGroup(): FormGroup {
		return new FormGroup({
			available: new FormControl(false),
			tutorial: new FormControl(false),
			autofill_from_ocr: new FormControl<boolean | null>(null),
			steps: new FormArray([]),
			uuid: new FormControl(''),
			completed: new FormControl(false),
		});
	}

	/**
	 * Schema OCR: fraud_scoring anidado (vía {@link formOcrFraudScoring}), docs/país, MiDNI, fallback.
	 *
	 * Abrir cuando: añades controles al paso OCR del editor.
	 *
	 * @returns FormGroup `operations.OCR`
	 */
	private buildOcrOperationFormGroup(): FormGroup {
		return new FormGroup({
			available: new FormControl(false),
			mode: new FormControl('auto', Validators.required),
			tutorial: new FormControl(true),
			fraud_scoring: this.formOcrFraudScoring,
			match_data: new FormControl(null),
			animations: new FormControl(true),
			setting: new FormGroup({
				total_tries: new FormControl(2, [Validators.required, Validators.min(1), Validators.max(4)]),
				side_max_time: new FormControl(150, [Validators.required, Validators.min(1)]),
				total_request: new FormControl(10, [Validators.required, Validators.min(1)]),
			}),
			dni_available: new FormControl(false),
			passport_available: new FormControl(false),
			nie_available: new FormControl(false),
			dni_type: new FormControl(OCR_DOC_COUNTRY_TYPE.ALL),
			dni_countries: new FormControl(''),
			is_schengen_unlimited: new FormControl(false),
			midni_validation_enabled: new FormControl(true),
			skip_pol_when_midni_authenticated: new FormControl(false),
			eudi_wallet_enabled: new FormControl(false),
			/** Default Figma: «Omitir paso» POL tras EUDI. */
			skip_pol_when_eudi_authenticated: new FormControl('true'),
			passport_type: new FormControl(OCR_DOC_COUNTRY_TYPE.ALL),
			passport_countries: new FormControl([]),
			required_check_nie: new FormControl(false),
			scan_barcode: new FormControl(false),
			obfuscation: new FormGroup({
				enabled: new FormControl(false),
				fields: new FormControl<string[]>([]),
				keep_unobfuscated_copy: new FormControl(true),
			}),
			document_watermark: new FormControl(false),
			fallback: new FormControl('auto'),
			fallback_validation: new FormControl('auto'),
			fallback_emails: new FormControl(''),
			uuid: new FormControl(''),
			completed: new FormControl(false),
		});
	}

	/**
	 * Schema POL: demand level, setting (tests/scoring), fraud_scoring facial, fallback.
	 *
	 * @returns FormGroup `operations.POL`
	 */
	private buildPolOperationFormGroup(): FormGroup {
		return new FormGroup({
			available: new FormControl(false),
			mode: new FormControl('', Validators.required),
			tutorial: new FormControl(true),
			type: new FormControl(DEMAND_LEVEL.STANDARD, Validators.required),
			setting: new FormGroup({
				total_tries: new FormControl(1, [Validators.required, Validators.min(1)]),
				type: new FormControl(POL_TYPE.RANDOM),
				total_test: new FormControl(1, [Validators.required, Validators.min(1), Validators.max(3)]),
				scoring: new FormControl(CONFIDENCE_THRESHOLD.MAX),
				scoring_custom: new FormControl(7, [
					Validators.required,
					Validators.pattern(/^(10([.,]0)?|[0-9]([.,][0-9])?)$/),
				]),
				available_tests: new FormControl([
					POL_TEST.MIDDLE_FACE,
					POL_TEST.SMILE,
					POL_TEST.TURN_RIGHT_HEAD,
					POL_TEST.TURN_LEFT_HEAD,
				]),
				max_time: new FormControl(300, [Validators.required, Validators.min(1)]),
			}),
			fraud_scoring: new FormGroup({
				facial_recognition: new FormControl(true),
				face_match: new FormControl(true),
				percentage_similarity: new FormControl(70, [
					Validators.required,
					Validators.min(0),
					Validators.max(100),
				]),
				anti_spoofing: new FormControl(true),
			}),
			fallback: new FormControl('auto'),
			fallback_validation: new FormControl('auto'),
			fallback_emails: new FormControl(''),
			uuid: new FormControl(''),
			completed: new FormControl(false),
		});
	}

	/**
	 * Schema UI de poderes notariales.
	 * El payload lo traduce a DOCUMENTS o CONNECTIONS (SCRIPTURE); no es tipo API propio.
	 *
	 * @returns FormGroup `operations.ATTORNEY_POWERS`
	 */
	private buildAttorneyPowersOperationFormGroup(): FormGroup {
		return new FormGroup({
			available: new FormControl(false),
			tutorial: new FormControl(true),
			total_tries: new FormControl(2, [Validators.required, Validators.min(1), Validators.max(4)]),
			title: new FormControl(''),
			mode: new FormControl('auto', Validators.required),
			fallback: new FormControl('manual'),
			fallback_validation: new FormControl('auto'),
			fallback_emails: new FormControl(''),
			uuid: new FormControl(''),
			completed: new FormControl(false),
		});
	}

	/**
	 * Schema DOCUMENTS: título opcional multi-locale + FormArray `docs`.
	 *
	 * @returns FormGroup `operations.DOCUMENTS`
	 */
	private buildDocumentsOperationFormGroup(): FormGroup {
		return new FormGroup({
			available: new FormControl(false),
			tutorial: new FormControl(true),
			custom_title: new FormControl(false),
			title: this.createLocaleTextFormGroup('', false),
			docs: new FormArray([], Validators.required),
			uuid: new FormControl(''),
			completed: new FormControl(false),
		});
	}

	/**
	 * Schema CONNECTIONS (evaluación financiera): tipo verify/scoring + lista de conexiones.
	 *
	 * @returns FormGroup `operations.CONNECTIONS`
	 */
	private buildConnectionsOperationFormGroup(): FormGroup {
		return new FormGroup({
			available: new FormControl(false),
			type: new FormControl('', Validators.required),
			connections: new FormControl([], Validators.required),
			total_tries: new FormControl(3, [Validators.required, Validators.min(1)]),
			uuid: new FormControl(''),
			completed: new FormControl(false),
		});
	}

	/**
	 * Schema FINISH: loading_text i18n + redirects/callbacks.
	 * `available` nace habilitado (la op FINISH siempre forma parte del journey).
	 *
	 * @returns FormGroup `operations.FINISH`
	 */
	private buildFinishOperationFormGroup(): FormGroup {
		// Mensajes vacíos = Identity defaults en runtime; si hay texto en un locale → todos (modal NIF-762).
		return new FormGroup({
			available: new FormControl({ value: true, disabled: false }),
			loading: new FormControl(true),
			show_ko_reason: new FormControl(true),
			loading_text: this.createLocaleTextFormGroup('', false),
			ok_title: this.createLocaleTextFormGroup('', false),
			ok_description: this.createLocaleTextFormGroup('', false),
			ko_title: this.createLocaleTextFormGroup('', false),
			ko_description: this.createLocaleTextFormGroup('', false),
			cancelled_title: this.createLocaleTextFormGroup('', false),
			cancelled_description: this.createLocaleTextFormGroup('', false),
			pending_title: this.createLocaleTextFormGroup('', false),
			pending_description: this.createLocaleTextFormGroup('', false),
			redirect: new FormControl({ value: '', disabled: false }),
			redirect_ko: new FormControl({ value: '', disabled: false }),
			redirect_cancelled: new FormControl({ value: '', disabled: false }),
			callback: new FormControl({ value: '', disabled: false }),
			callback_ko: new FormControl({ value: '', disabled: false }),
			callback_cancelled: new FormControl({ value: '', disabled: false }),
			uuid: new FormControl(''),
			completed: new FormControl(false),
		});
	}

	/** Borrador del modal de mensajes de resultado FINISH (NIF-762). */
	public buildFinishResultMessagesFormGroup(): FormGroup {
		return new FormGroup({
			loading_text: this.createLocaleTextFormGroup('', false),
			ok_title: this.createLocaleTextFormGroup('', false),
			ok_description: this.createLocaleTextFormGroup('', false),
			ko_title: this.createLocaleTextFormGroup('', false),
			ko_description: this.createLocaleTextFormGroup('', false),
			cancelled_title: this.createLocaleTextFormGroup('', false),
			cancelled_description: this.createLocaleTextFormGroup('', false),
			pending_title: this.createLocaleTextFormGroup('', false),
			pending_description: this.createLocaleTextFormGroup('', false),
		});
	}

	/**
	 * FormGroup del modal de scoring antifraude OCR.
	 * Incluye flags AEAT/AML/PEP y los checks anidados (manipulated_photo, photocopy, …).
	 * Cada llamada crea una instancia nueva (se embebe en `operations.OCR.fraud_scoring`).
	 *
	 * @returns FormGroup listo para el modal / patch OCR
	 */
	public get formOcrFraudScoring(): FormGroup {
		return new FormGroup({
			aeat: new FormControl('true'),
			legal_age: new FormControl(18),
			expirated: new FormControl(true),
			valid_country: new FormControl(true),
			ocr_allowed_expiration: new FormControl(0, [Validators.required, Validators.min(0), Validators.max(7)]),
			validate_address: new FormControl(false),
			validate_team_number: new FormControl(false),
			aml: new FormControl(''),
			pep: new FormControl(''),
			type: new FormControl(DEMAND_LEVEL.RECOMMENDED, Validators.required),
			// Cada check: available + blocking/ip/identifier + email opcional
			manipulated_photo: new FormGroup({
				available: new FormControl(true),
				blocking_step: new FormControl(true),
				ip_block: new FormControl(false),
				identifier_block: new FormControl(false),
				email_alert_available: new FormControl(false),
				email_alert: new FormControl(''),
			}),
			photocopy_check: new FormGroup({
				available: new FormControl(true),
				blocking_step: new FormControl(true),
				ip_block: new FormControl(false),
				identifier_block: new FormControl(false),
				email_alert_available: new FormControl(false),
				email_alert: new FormControl(''),
			}),
			screen_photo: new FormGroup({
				available: new FormControl(true),
				blocking_step: new FormControl(true),
				ip_block: new FormControl(false),
				identifier_block: new FormControl(false),
				email_alert_available: new FormControl(false),
				email_alert: new FormControl(''),
			}),
			identifier_blocked: new FormGroup({
				available: new FormControl(true),
				blocking_step: new FormControl(true),
				ip_block: new FormControl(false),
				identifier_block: new FormControl(false),
				email_alert_available: new FormControl(false),
				email_alert: new FormControl(''),
			}),
		});
	}

	/**
	 * FormGroup del modal de documento (título/pautas multi-locale + config de captura).
	 *
	 * @returns Nueva instancia para alta/edición de una fila DOCUMENTS
	 */
	public get formDoc(): FormGroup {
		return new FormGroup({
			type: new FormControl('', Validators.required),
			required: new FormControl(true, Validators.required),
			method: new FormControl(['manual', 'photo'], Validators.required),
			total_tries: new FormControl(null),
			min: new FormControl(null),
			max: new FormControl(null),
			instructions: this.createDocInstructionsLocaleFormGroup(),
			instructions_photo: this.createLocaleTextFormGroup(),
			title: this.createDocTitleLocaleFormGroup(),
			description: this.createLocaleTextFormGroup('', true),
			preview_validation: new FormControl(null),
			match_data: new FormControl(null),
			icon: new FormControl(null),
			demand_level: new FormControl(DEMAND_LEVEL.RECOMMENDED),
			docInfo: new FormControl(null),
			uuid: new FormControl(''),
		});
	}

	/**
	 * FormGroup de un campo FORM.
	 * `match_ocr` es control UI; al serializar se fusiona en `validators[]` (ver {@link serializeFormField}).
	 *
	 * @returns Nueva instancia de campo
	 */
	public get formFormField(): FormGroup {
		return new FormGroup({
			uuid: new FormControl(''),
			id: new FormControl('', Validators.required),
			name: new FormControl('', Validators.required),
			type: new FormControl('text', Validators.required),
			title: this.createLocaleTextFormGroup('', false),
			description: new FormControl(''),
			col: new FormControl(12, [Validators.required, Validators.min(1), Validators.max(12)]),
			validators: new FormControl<string[]>([]),
			match_ocr: new FormControl(false),
			options: new FormArray([]),
		});
	}

	/**
	 * Alias de {@link createFormFieldOptionGroup} sin rich-text.
	 *
	 * @returns Fila de opción (value + label multi-locale)
	 */
	public get formFormFieldOption(): FormGroup {
		return this.createFormFieldOptionGroup(false);
	}

	/**
	 * Fila de opción FORM: `label` es FormGroup por idioma (obligatorio en todos los locales).
	 *
	 * @param richText - Si `true`, usa validador rich-text (HTML) en cada locale
	 * @returns FormGroup `{ value, label: { es, en, … } }`
	 */
	public createFormFieldOptionGroup(richText = false): FormGroup {
		const allowed = this.langSvc.getAllowedLanguages();
		const labelValidators = richText
			? [trimmedRichTextLocaleRequiredValidator()]
			: [trimmedLocaleTextRequiredValidator()];
		const labelControls: Record<string, FormControl> = {};

		for (const locale of allowed) {
			labelControls[locale] = new FormControl('', labelValidators);
		}

		return new FormGroup({
			value: new FormControl(''),
			label: new FormGroup(labelControls),
		});
	}

	/**
	 * FormGroup de un paso FORM (pauta/descripción multi-locale + `fields[]`).
	 * Sin aplicar aún validators de pauta — ver {@link createFormStepGroup}.
	 *
	 * @returns Paso “crudo” (sin validators Figma de pauta/descripción)
	 */
	public get formFormStep(): FormGroup {
		return new FormGroup({
			uuid: new FormControl(''),
			step_id: new FormControl(1, [Validators.required, Validators.min(1)]),
			// Pauta* y Descripción: grupos por locale; obligatoriedad según Figma en applyFormStep*Validators.
			step_name: this.createLocaleTextFormGroup('', false),
			step_description: this.createLocaleTextFormGroup('', false),
			alert_msg: new FormControl(''),
			fields: new FormArray([], Validators.required),
		});
	}

	/**
	 * Nueva instancia de campo FORM (equivale a {@link formFormField}).
	 *
	 * @returns FormGroup de campo
	 */
	public createFormFieldGroup(): FormGroup {
		return this.formFormField;
	}

	/**
	 * Paso FORM listo para el editor: aplica validators Figma de pauta* y descripción.
	 *
	 * @returns FormGroup de paso con validación de locales ya configurada
	 */
	public createFormStepGroup(): FormGroup {
		const stepGroup = this.formFormStep;
		this.applyFormStepGuidelineValidatorsForPreset(stepGroup.get('step_name') as FormGroup, null);
		this.applyFormStepDescriptionValidators(stepGroup.get('step_description') as FormGroup);
		return stepGroup;
	}

	/**
	 * Indica si el campo debe validar contra OCR
	 * (`validators[]` contiene `match_ocr` o el control UI `match_ocr === true`).
	 *
	 * @param field - Valores del campo (API o form)
	 * @returns `true` si hay que cotejar con OCR
	 */
	public resolveMatchOcrFlag(field: Record<string, unknown>): boolean {
		return resolveMatchOcrFlagUtil(field);
	}

	/**
	 * Prepara valores para `patchValue` del FormGroup de campo:
	 * separa el flag UI `match_ocr` de `validators[]` (quita `match_ocr` del array).
	 *
	 * @param field - Campo crudo del API
	 * @returns Objeto listo para patch del form (con `match_ocr` booleano)
	 */
	public patchFormFieldValue(field: Record<string, unknown>): Record<string, unknown> {
		const allowed = this.langSvc.getAllowedLanguages();
		const patched = patchFormFieldFromApi(field, allowed);
		const validators = (patched['validators'] as string[]) ?? [];
		return {
			...patched,
			match_ocr: resolveMatchOcrFlagUtil(patched),
			validators: stripMatchOcrFromValidators(validators),
		};
	}

	/**
	 * Serializa un campo FORM para API: uuid, title i18n, y `match_ocr` dentro de `validators[]`.
	 *
	 * @param field - Valor del form (puede traer `match_ocr` UI)
	 * @returns Campo listo para el payload (`serializeFormFieldToApi`)
	 */
	public serializeFormField(field: Record<string, unknown>): Record<string, unknown> {
		const allowed = this.langSvc.getAllowedLanguages();
		return serializeFormFieldToApi(withMatchOcrInValidators(field), allowed);
	}
}
